import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';
import { EnvelopeIcon, PhoneIcon, MapMarkerAltIcon, RocketIcon } from './IconComponents';
import { trackEvent } from '../analytics';

const ContactSection = styled.section`
  position: relative;
  padding: 6rem 0;
  background-color: var(--background-color);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -150px;
    left: 50%;
    transform: translateX(-50%);
    width: 700px;
    height: 700px;
    background: radial-gradient(circle, rgba(250, 63, 59, 0.12) 0%, rgba(250, 63, 59, 0) 70%);
    pointer-events: none;
  }
`;

const SectionTitle = styled.h2`
  text-align: left;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }

  @media (max-width: 992px) {
    text-align: center;
  }
`;

const SectionSubtitle = styled.p`
  text-align: left;
  color: var(--text-color);
  font-size: 1.1rem;

  @media (max-width: 992px) {
    text-align: center;
  }
`;

const ContactContainer = styled.div`
  position: relative;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ContactTextColumn = styled.div`
  @media (max-width: 992px) {
    order: 1;
  }
`;

const ContactIllustration = styled.div`
  position: relative;
  margin-top: 3rem;
  width: 260px;
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 992px) {
    margin: 3rem auto 0;
  }
`;

const IllustrationCircle = styled.div`
  position: absolute;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(250, 63, 59, 0.18) 0%, rgba(250, 63, 59, 0.02) 70%);
  border: 1px solid rgba(250, 63, 59, 0.25);
`;

const IllustrationCenterIcon = styled.div`
  position: relative;
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 30px rgba(250, 63, 59, 0.4);

  svg {
    font-size: 2.2rem;
    color: var(--light-text-color);
  }
`;

const FloatingIcon = styled.div<{ top: string; left: string; delay: string }>`
  position: absolute;
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background-color: var(--card-background);
  border: 1px solid #333333;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: floatIcon 4s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay};

  svg {
    font-size: 1.3rem;
    color: var(--primary-color);
  }

  @keyframes floatIcon {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
`;

const ContactForm = styled.form`
  position: relative;
  background-color: var(--card-background);
  padding: 3.5rem;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
  border: 1px solid #333333;
  border-top: 4px solid var(--primary-color);

  @media (max-width: 576px) {
    padding: 2.5rem 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-color);
`;

const FormInput = styled.input`
  width: 100%; 
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
  border: 1px solid #444444;
  border-radius: 4px; 
  font-size: 1rem;
  font-family: inherit;
  background-color: var(--card-background);
  color: var(--text-color);
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(250, 63, 59, 0.2);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
  border: 1px solid #444444;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 150px;
  background-color: var(--card-background);
  color: var(--text-color);
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(250, 63, 59, 0.2);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: var(--primary-color);
  color: var(--light-text-color);
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-2px);
  }

  &:disabled {
    background-color: #555555;
    cursor: not-allowed;
    transform: none;
  }
`;

const FormMessage = styled.div<{ success?: boolean }>`
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.9rem;
  background-color: ${({ success }) => success ? '#d4edda' : '#f8d7da'};
  color: ${({ success }) => success ? '#155724' : '#721c24'};
  display: ${({ success }) => success === undefined ? 'none' : 'block'};
`;

const Contact: React.FC = () => {
  const { t, i18n } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState<{ success?: boolean, message: string }>({
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ message: '' });

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('subject', formData.subject);
      body.append('message', formData.message);

      const response = await fetch('/mail.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body.toString()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        trackEvent('Contact', 'submit_form', formData.subject);
        setFormStatus({
          success: true,
          message: t('contact.form.success_message')
        });
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setFormStatus({
          success: false,
          message: data.message || t('contact.form.error_message')
        });
      }
    } catch (error) {
      setFormStatus({
        success: false,
        message: t('contact.form.error_message')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection id="contact">
      <ContactContainer>
        <ContactTextColumn>
          <SectionTitle>
            <Trans i18nKey="contact.title">
              Get in <span>Touch</span>
            </Trans>
          </SectionTitle>
          <SectionSubtitle>
            {t('contact.subtitle')}s
          </SectionSubtitle>
          <ContactIllustration>
            <IllustrationCircle />
            <FloatingIcon top="0" left="10%" delay="0s">
              <EnvelopeIcon />
            </FloatingIcon>
            <FloatingIcon top="20%" left="75%" delay="0.8s">
              <PhoneIcon />
            </FloatingIcon>
            <FloatingIcon top="75%" left="70%" delay="1.6s">
              <MapMarkerAltIcon />
            </FloatingIcon>
            <IllustrationCenterIcon>
              <RocketIcon />
            </IllustrationCenterIcon>
          </ContactIllustration>
        </ContactTextColumn>
        <ContactForm onSubmit={handleSubmit}>
          <FormGroup>
            <FormLabel htmlFor="name">{t('contact.form.name')}</FormLabel>
            <FormInput 
              type="text" 
              id="name" 
              name="name" 
              value={formData.name} 
              onChange={handleChange} 
              required 
            />
          </FormGroup>
          <FormGroup>
            <FormLabel htmlFor="email">{t('contact.form.email')}</FormLabel>
            <FormInput 
              type="email" 
              id="email" 
              name="email" 
              value={formData.email} 
              onChange={handleChange} 
              required 
            />
          </FormGroup>
          <FormGroup>
            <FormLabel htmlFor="subject">{t('contact.form.subject')}</FormLabel>
            <FormInput 
              type="text" 
              id="subject" 
              name="subject" 
              value={formData.subject} 
              onChange={handleChange} 
              required 
            />
          </FormGroup>
          <FormGroup>
            <FormLabel htmlFor="message">{t('contact.form.message')}</FormLabel>
            <FormTextarea 
              id="message" 
              name="message" 
              value={formData.message} 
              onChange={handleChange} 
              required 
            />
          </FormGroup>
          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('contact.form.sending') : t('contact.form.send')}
          </SubmitButton>
          {formStatus.message && (
            <FormMessage success={formStatus.success}>
              {formStatus.message}
            </FormMessage>
          )}
        </ContactForm>
      </ContactContainer>
    </ContactSection>
  );
};

export default Contact;
